import { Button, Modal } from '@/components/ui';
import { ONBOARDING_COPY } from '@/pages/onboarding/onboardingCopy';

export interface SignOutConfirmModalProps {
  open: boolean;
  /** While the sign-out request is in flight: both buttons disabled, confirm shows its spinner. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The sign-out confirmation shared by the fork and the join wait-state
 * (YAR-439 AC 6). Figma "Fork / default / Sign out" and "Join household /
 * default / Sign out": a centred card, title and body centred, primary
 * "Sign out" over a tertiary "Cancel". Same card geometry as the invitee
 * decline modal — 20 (xl) padding, 16 (lg) between text and buttons, 6 (2xs)
 * inside the text block, 12 (md) between the buttons. The title is
 * `text/content/heading` (#1a1a1a) over a `text/content/body` line, the
 * same two-tone pairing every onboarding heading uses (QA on YAR-438
 * measured the first cut's title at body #2f2f2f).
 */
export function SignOutConfirmModal({
  open,
  busy = false,
  onConfirm,
  onCancel,
}: SignOutConfirmModalProps) {
  const copy = ONBOARDING_COPY.signOutModal;
  return (
    <Modal
      open={open}
      onClose={onCancel}
      ariaLabel={copy.title}
      bare
      dismissOnBackdrop={!busy}
    >
      <div className="flex flex-col gap-4 p-5">
        <div className="flex flex-col gap-1.5 text-center">
          <h2 className="text-xl leading-7 font-medium text-text-heading">
            {copy.title}
          </h2>
          <p className="text-base leading-6 text-foreground">{copy.body}</p>
        </div>
        <div className="flex flex-col gap-3">
          <Button
            variant="primary"
            fullWidth
            loading={busy}
            disabled={busy}
            onClick={onConfirm}
          >
            {copy.confirm}
          </Button>
          <Button
            variant="tertiary"
            fullWidth
            disabled={busy}
            onClick={onCancel}
          >
            {copy.cancel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
